import React, { useState, useEffect } from "react";
import StatusPill from "../components/common/StatusPill";
import { useParams, useNavigate } from "react-router-dom";
import {
  Save,
  AlertCircle,
  CheckCircle,
  Stethoscope,
  User,
  Loader2,
  RefreshCw,
} from "lucide-react";
import LoadingSpinner from "../components/common/LoadingSpinner";
import DateTimePicker from "../components/common/DateTimePicker";
import DoctorSearchDropdown from "../components/consultations/DoctorSearchDropdown";
import consultationService from "../services/consultationService";
import { getDoctors } from "../services/doctorService";
import PageHeader from "../components/common/PageHeader";
import InfoItem from "../components/common/InfoItem";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

// Helper function to format doctor name with Dr. prefix
const formatDoctorName = (name, specialization) => {
  if (!name) return "N/A";

  // Don't add Dr. prefix for Psychology specialty
  if (specialization && specialization.toLowerCase().includes("psychology")) {
    return name;
  }

  // Add Dr. prefix for all other specializations (case-insensitive check)
  const nameLower = name.toLowerCase();
  if (nameLower.startsWith("dr. ") || nameLower.startsWith("dr ")) {
    return name;
  }
  return `Dr. ${name}`;
};

const EditConsultation = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]); // Store booked timeslots
  const [formData, setFormData] = useState({
    consultationTime: null,
    doctorId: "",
    doctorName: "",
    doctorDetails: null,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (consultationId) {
      fetchConsultationDetails();
      loadDoctors();
    }
  }, [consultationId]);

  // Load booked consultations for a specific date (excluding current consultation)
  const loadBookedTimes = async (date) => {
    if (!date) return;
    
    try {
      const result = await consultationService.getConsultationsByDate(date, 100);
      if (result && result.consultations) {
        // Extract consultation times, excluding the current consultation being edited
        const times = result.consultations
          .filter(c => {
            // Exclude current consultation being edited
            if (c.id === consultationId) return false;
            // Only exclude if explicitly cancelled
            const isCancelled = c.cancelledByDoctor === true || c.cancelledByPatient === true;
            return !isCancelled;
          })
          .map(c => {
            const time = new Date(c.consultationTime);
            return time;
          })
          .filter(t => !isNaN(t.getTime()));
        
        setBookedTimes(times);
      }
    } catch (error) {
      console.error("Error loading booked times:", error);
    }
  };

  // Handle date change from DateTimePicker
  const handleDatePickerDateChange = (date) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      loadBookedTimes(date);
    }
  };

  const fetchConsultationDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const detailedData = await consultationService.getConsultationById(
        consultationId
      );
      const formattedData =
        consultationService.formatConsultationData(detailedData);
      setConsultation(formattedData);

      // Initialize form data
      const consultationTime = formattedData.consultationTime
        ? new Date(formattedData.consultationTime)
        : null;

      setFormData({
        consultationTime,
        doctorId: formattedData.participants?.[1] || "",
        doctorName: formattedData.doctorName || "",
        doctorDetails: formattedData.doctorDetails || null,
      });
      
      // Load booked times for the consultation date
      if (consultationTime) {
        loadBookedTimes(consultationTime);
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching consultation details:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      setDoctorsLoading(true);
      const result = await getDoctors();
      if (result.success) {
        setDoctors(result.data || []);
      } else {
        console.error("Failed to load doctors:", result.error);
      }
    } catch (error) {
      console.error("Error loading doctors:", error);
    } finally {
      setDoctorsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleDoctorSelect = (selectedDoctor) => {
    if (selectedDoctor) {
      setFormData((prev) => ({
        ...prev,
        doctorId: selectedDoctor.uid,
        doctorName: selectedDoctor.name,
        doctorDetails: {
          specialty: selectedDoctor.specialization,
          experience: selectedDoctor.numExp,
          worksAt: selectedDoctor.worksAt,
          profileImage: selectedDoctor.profileImage,
        },
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.consultationTime) {
      newErrors.consultationTime = "Consultation time is required";
    } else {
      const selectedTime = formData.consultationTime.getTime();
      const now = new Date().getTime();
      if (selectedTime <= now) {
        newErrors.consultationTime = "Consultation time must be in the future";
      }
    }

    if (!formData.doctorId) {
      newErrors.doctorId = "Please select a doctor";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        consultationTime: formData.consultationTime.getTime(),
        doctorId: formData.doctorId,
        doctorName: formData.doctorName,
        doctorDetails: formData.doctorDetails,
      };

      const updatedConsultation = await consultationService.updateConsultation(
        consultationId,
        updateData
      );

      // Format the updated consultation data
      const formattedData =
        consultationService.formatConsultationData(updatedConsultation);
      setConsultation(formattedData);

      setSuccess(true);
      setTimeout(() => {
        navigate(`/consultations/${consultationId}`);
      }, 2000);
    } catch (error) {
      console.error("Error updating consultation:", error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGoBack = () => {
    navigate(`/consultations/${consultationId}`);
  };

  const backTitle = (
    <PageHeader title="Edit consultation" onBack={handleGoBack} />
  );

  if (loading) {
    return (
      <div>
        {backTitle}
        <LoadingSpinner message="Loading consultation details..." />
      </div>
    );
  }

  if (error && !consultation) {
    return (
      <div>
        {backTitle}
        <Card className="mx-auto max-w-md">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <h3 className="text-lg font-semibold">Error loading consultation</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={fetchConsultationDetails}>
              <RefreshCw /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!consultation) {
    return (
      <div>
        {backTitle}
        <Card className="mx-auto max-w-md">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Consultation not found</h3>
            <p className="text-sm text-muted-foreground">The requested consultation could not be found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHeader
        title="Edit consultation"
        description={`Update time and doctor for consultation #${consultation.consultationId?.slice(-8) || "N/A"}`}
        onBack={handleGoBack}
        actions={
          !success && (
            <Button
              onClick={handleSave}
              disabled={saving || !formData.consultationTime || !formData.doctorId}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save /> Save changes
                </>
              )}
            </Button>
          )
        }
      />

      {success ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <CheckCircle className="h-10 w-10 text-emerald-600" />
            <h3 className="text-lg font-semibold">Consultation updated</h3>
            <p className="text-sm text-muted-foreground">
              Your changes have been saved. Redirecting to consultation details...
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Schedule and doctor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="form-group">
                <DateTimePicker
                  value={formData.consultationTime}
                  onChange={(date) => handleInputChange("consultationTime", date)}
                  onDateChange={handleDatePickerDateChange}
                  excludeTimes={bookedTimes}
                  placeholder="Select consultation date and time"
                  disabled={saving}
                  error={errors.consultationTime}
                  minDate={new Date()}
                  showTimeSelect={true}
                  slotDuration={55}
                  dateFormat="MMM dd, yyyy hh:mm:aa"
                  timeFormat="hh:mm:aa"
                />
                {errors.consultationTime && (
                  <p className="mt-1.5 text-xs font-medium text-destructive">{errors.consultationTime}</p>
                )}
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-medium">
                  <Stethoscope className="h-4 w-4 text-muted-foreground" />
                  Assign doctor
                </label>
                <DoctorSearchDropdown
                  doctors={doctors}
                  selectedDoctorId={formData.doctorId}
                  onDoctorSelect={handleDoctorSelect}
                  loading={doctorsLoading}
                  error={errors.doctorId}
                  placeholder="Search and select a doctor..."
                />
                {errors.doctorId && (
                  <p className="mt-1.5 text-xs font-medium text-destructive">{errors.doctorId}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {formData.doctorDetails && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base">Selected doctor</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent text-accent-foreground">
                    {formData.doctorDetails.profileImage ? (
                      <img
                        src={formData.doctorDetails.profileImage}
                        alt={formData.doctorName}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold">
                      {formatDoctorName(formData.doctorName, formData.doctorDetails.specialty)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formData.doctorDetails.specialty}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                      {formData.doctorDetails.experience && (
                        <span>{formData.doctorDetails.experience} years experience</span>
                      )}
                      {formData.doctorDetails.worksAt && <span>{formData.doctorDetails.worksAt}</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Current consultation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-3">
                <InfoItem label="Current time" value={consultation.formattedConsultationTime} />
                <InfoItem label="Current doctor" value={consultation.doctorName || "Not assigned"} />
                <div className="rounded-lg border bg-muted/30 p-3.5">
                  <p className="text-xs font-medium text-muted-foreground">Status</p>
                  <div style={{ marginTop: 4 }}><StatusPill style={{ textTransform: "capitalize" }}>{consultation.status}</StatusPill></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default EditConsultation;
